import { mapActions, mapState } from 'vuex'
import firebase from 'firebase/app'
import 'firebase/messaging'
import axios from 'axios'
import Promise from 'bluebird'
import fcmReadyChecker from 'components/mixins/fcmReadyChecker'
import storage from 'local-storage-fallback'
import get from 'lodash/get'
import { localStorageAvailable } from 'mixins/utilities'

export default{
  mixins: [fcmReadyChecker],
  data() {
    return {
      servieWorkerReady: false,
      firebasePushMessaging: null,
    }
  },
  computed: {
    ...mapState([
      'isNativeWrapper',
    ]),
    isNotificationCenterEnabled() {
      return this.servieWorkerReady || this.isNativeWrapper
    },
  },
  methods: {
    ...mapActions(['setNotificationCenterStatus', 'setPushPayloadMsg']),
    fcmInit() {
      const self = this
      if (this.isNativeWrapper) {
        this.setNativeWrapperStatus()
      } else if (localStorageAvailable()) {
        if (this.fcmReady()) {
          navigator.serviceWorker.ready.then((registration) => {
            self.registration = registration
            self.servieWorkerReady = true
            self.setInitStatus()
          }).catch((err) => {
            console.log('Service Worker error: ', err)
          })
        } else {
          console.log('Service workers are not supported.')
        }
      } else {
        console.log('Allow access to local storage to enable the Notification Center')
      }
    },
    fcmTrigger() {
      if (this.isNativeWrapper) {
        window.nativeBridge.call('subscribePushNotification', (success) => {
          if (success) {
            this.setNotificationCenterStatus('granted')
          }
        })
      } else if (this.fcmReady()) {
        navigator.serviceWorker.ready.then((registration) => {
          this.registration = registration
          this.startMessaging()
        }).catch((err) => {
          console.log('Service Worker error: ', err)
        })
      }
    },
    fcmUnsubscribe({updateStatus = true}) {
      if (this.isNativeWrapper) {
        window.nativeBridge.call('unsubscribePushNotification')
        this.setNotificationCenterStatus('unsubscribe_success')
        return Promise.resolve(true)
      }
      return new Promise((resolve, reject) => {
        // fcmUnsubscribe() is called in subscribe(), no need to updateStatus there since no dialog needs to change.
        // @TODO: We should add loading and trigger update status, for now will assume unsubscribed.
        if (updateStatus) {
          this.setNotificationCenterStatus('unsubscribe_success')
        }
        let unsubscribePromise
        if (!this.getLocalStorageToken()) {
          unsubscribePromise = this.getTokenFromServiceWorker()
        } else {
          unsubscribePromise = Promise.resolve(this.getLocalStorageToken())
        }

        unsubscribePromise.then((token) => {
          axios.post('/api/unsubscribe-fcm', {
            token,
          }).then(() => {
            storage.removeItem('fcmToken')
            storage.setItem('fcmSubscribed', 'no')
            resolve(true)
          }).catch((e) => {
            reject(e)
          })
        })
      })
    },
    setInitStatus() {
      const self = this
      // To show which dialog layer and bell indicators to show when landing on page
      if (this.getLocalStorageToken()) {
        // Unsubscibe token on init for default for fresh start, will get token anyways once granted
        if (Notification.permission === 'default') {
          this.fcmUnsubscribe({updateStatus: false})
        }

        self.setNotificationCenterStatus(Notification.permission)
      } else {
        // No token in LocalStorage
        if (Notification.permission === 'denied') {
          self.setNotificationCenterStatus('denied')
          return
        }
        // Back to default so user can request permission again and generate fcmToken
        self.setNotificationCenterStatus('default')
      }
    },
    setNativeWrapperStatus() {
      if (get(window.nativeBridge.call('getAppStatus'), 'is_push_notification_on') && get(window.nativeBridge.call('getAppStatus'), 'is_system_push_notification_on')) {
        this.setNotificationCenterStatus('granted')
      } else if (get(window.nativeBridge.call('getAppStatus'), 'is_system_push_notification_on')) {
        this.setNotificationCenterStatus('default')
      }
    },
    getLocalStorageToken() {
      if (!this.fcmReady()) return false
      return storage.getItem('fcmToken') || false
    },
    getLocalStorageSubscribed() {
      if (!this.fcmReady()) return false
      return storage.getItem('fcmSubscribed') === 'yes' // checked in PushPopup.vue whether to trigger re-subscribe or not
    },
    doSubscribe(token) {
      // @TODO: We should check the AJAX response to make sure subscribe correctly.
      storage.setItem('fcmToken', token)
      storage.setItem('fcmSubscribed', 'yes')
      axios.post('/api/subscribe-fcm', {
        token,
      })
      this.setNotificationCenterStatus(Notification.permission) // 'granted'
    },
    subscribe(token, force = false) {
      if (!this.fcmReady()) return
      if (storage.getItem('fcmSubscribed') !== 'yes' || force || this.getLocalStorageToken() !== token) {
        this.fcmUnsubscribe({updateStatus: false}).then(() => {
          this.doSubscribe(token)
          return true
        }).catch((e) => {
          console.log(e)
          // Some issue when unsubscribe, but will try subscribe anyway...
          this.doSubscribe(token)
          return true
        })
      }
    },
    requestPermissionAndSubscribe(force = false) {
      // If granted, requestPermission(), will skip the browser promt and then use callback
      this.firebasePushMessaging.requestPermission().then(() => {
        this.getPushMessagingTokenAndSubscribe(force)
        this.setNotificationCenterStatus(Notification.permission) // 'granted' / 'default'
      }).catch(() => {
        // cancel or block handling
        this.setNotificationCenterStatus(Notification.permission) // 'denied'
      })
    },
    getTokenFromServiceWorker() {
      return this.firebasePushMessaging ? this.firebasePushMessaging.getToken() : Promise.reject(new Error('No Firebase messing object'))
    },
    getPushMessagingTokenAndSubscribe(force = false) {
      this.getTokenFromServiceWorker().then((token) => {
        this.subscribe(token, force)
      }).catch((e) => {
        console.log(e)
        // some reason cannot retrieve token, eg.  site without ssl
      })
    },
    startMessaging(force = false) {
      // Check firebase
      if (!firebase.apps.length) firebase.initializeApp(this.config.FCM_CONFIG)

      // Set firebasePushMessaging if missing
      if (!this.firebasePushMessaging) {
        this.firebasePushMessaging = firebase.messaging()
        this.firebasePushMessaging.useServiceWorker(this.registration)
      }

      // Recieve push payload message on active tab
      this.firebasePushMessaging.onMessage((payload) => {
        if (storage.getItem('fcmSubscribed') === 'yes') {
          this.setPushPayloadMsg(payload)
        }
      })

      // Callback for token refresh suggested by firebase (standard code)
      this.firebasePushMessaging.onTokenRefresh(() => {
        this.getTokenFromServiceWorker().then((refreshedToken) => {
          this.subscribe(refreshedToken)
        }).catch((err) => {
          console.log('Unable to retrieve refreshed token ', err)
        })
      })
      this.requestPermissionAndSubscribe(force)
    },
  },
  watch: {
    isNativeWrapper() {
      this.setNativeWrapperStatus()
    },
  },
}



// WEBPACK FOOTER //
// ./components/mixins/firebasePush.js